<script lang="ts">
	import { browser } from '$app/environment';
	import { fly } from 'svelte/transition';
	import LanguageDropdown from '../UI/LanguageDropdown.svelte';
	import { expoInOut, expoOut } from 'svelte/easing';
	import Blur from '../Blur.svelte';
	import { onMount } from 'svelte';
	import { page } from '$app/state';
	import ProfileDropdown from '../UI/ProfileDropdown.svelte';
	import type { Session, SupabaseClient } from '@supabase/supabase-js';

	import { t } from '@riva-mono/common';
	import { env } from '$env/dynamic/public';

	interface Props {
		session: Session | null | undefined;
		supabase: SupabaseClient<any, 'public', any> | undefined;
	}

	let { session, supabase }: Props = $props();

	// const userStore: UserStore = getContext('user');
	const LIGHT_NAV_URLS = ['team/[id]', 'terms'];

	let scrollY = 0;
	let smallNav = $state(false);
	let showMobileMenu = $state(false);
	let footer: HTMLElement | undefined = $state();
	let profileDialog: HTMLDialogElement | null;
	let hideNav = $state(false);

	function toggleMenu() {
		showMobileMenu = !showMobileMenu;
	}

	function checkSmallNav(scrollY: number, routeId: string | null) {
		if (LIGHT_NAV_URLS.some((str) => routeId?.includes(str))) {
			smallNav = true;
		} else {
			smallNav = scrollY >= 50;
		}
	}

	function preventDefault(event: Event) {
		event.preventDefault();
	}

	function onScroll() {
		if (!footer) {
			return;
		}
		const bbox = footer.getBoundingClientRect();
		if (bbox?.top <= 120) {
			hideNav = true;
		} else {
			hideNav = false;
		}
	}

	function showProfile() {
		profileDialog?.showModal();
	}

	onMount(() => {
		footer = document.querySelector('footer') as HTMLElement;
		// window.addEventListener('scroll', onScroll);
		profileDialog = document.querySelector('#profileDialog');
	});
	let pages = $derived([
		{ title: $t('common.navigation.bloodtest'), href: `bloodtest` },
		{ title: $t('common.navigation.checkups'), href: `checkups` },
		{ title: $t('common.navigation.consultations'), href: `care` }
	]);
	let hideLanguages = $derived(page.url.pathname.includes('teasing'));

	if (page.route.id) {
		showMobileMenu = false;
	}
	$effect(() => {
		checkSmallNav(scrollY, page.route.id);
		if (browser) {
			if (showMobileMenu) {
				smallNav = true;
				document.body.addEventListener('touchmove', preventDefault);
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
				document.body.removeEventListener('touchmove', preventDefault);
				checkSmallNav(scrollY, page.route.id);
			}
		}

		if (browser && page.url?.href) {
			footer = document.querySelector('footer') as HTMLElement;
		}
	});
</script>

<nav
	class="logoNav"
	in:fly={{ y: -100, opacity: 1, duration: 800, delay: 200, easing: expoOut }}
	out:fly={{ y: -100, opacity: 0, duration: 500, easing: expoInOut }}
>
	<div class="container">
		{#if showMobileMenu}
			<div
				style="justify-content: space-between; "
				class="mobileMenu"
				transition:fly={{ y: -100, easing: expoOut }}
			>
				<div class="items-container">
					{#each pages as item}
						<a
							class="navLink"
							href="{env.PUBLIC_WEBSITE_URL}/{page.params.lang}/{item.href}"
							class:selected={page.url.pathname.includes(`/${item.href}`)}>{$t(item.title)}</a
						>
					{/each}
					{#if supabase && session}
						<!-- <button class="navLink" onclick={showProfile} class:disabled={!$userStore.onboarded}
								>{$t('common.navigation.profile')}
							</button> -->

						<a href="/signout" class="navLink">{$t('common.navigation.logout')}</a>
					{/if}
				</div>
				<div class="row" style="width:100%; ">
					{#if !page.data.session}
						<a onclick={toggleMenu} href="/{page.params.lang}/booking" class="button primary"
							>{$t('common.navigation.login')}</a
						>
					{:else}
						<a onclick={toggleMenu} href="/{page.params.lang}/booking" class="button primary"
							>{$t('common.navigation.dashboard')}</a
						>
					{/if}
					{#if !hideLanguages}
						<LanguageDropdown direction="up" />
					{/if}
				</div>
			</div>
		{/if}

		<a
			class="logo-container"
			href={page.route.id?.endsWith('/booking')
				? `${env.PUBLIC_WEBSITE_URL}/${page.params.lang}/`
				: `/${page.params.lang}/booking`}
			><img src="/logo-riva.svg" width="100" alt="Logo Riva" class="logo" /></a
		>

		<div class="navigation pages" style="position:absolute; left: 50%; transform:translateX(-50%);">
			{#each pages as item}
				<a
					class="navLink"
					href={`${env.PUBLIC_WEBSITE_URL}/${page.params.lang}/${item.href}`}
					class:selected={page.url.pathname.includes(`/${item.href}`)}>{$t(item.title)}</a
				>
			{/each}
		</div>
		<div class="navigation">
			<LanguageDropdown />
			{#if !page.data.session}
				<a class="button primary" href="/{page.params.lang}/booking"
					>{$t('common.navigation.login')}</a
				>
			{/if}
			{#if session}
				<ProfileDropdown />
			{/if}
		</div>
		<button class="burgerMenu" onclick={toggleMenu} aria-label="Menu">
			<div
				class="line"
				style:transform={showMobileMenu ? ' rotate(45deg) translateY(0px)' : 'translateY(-4px)'}
				style:background-color={'var(--burgundy)'}
			></div>
			<div
				class="line"
				style:transform={showMobileMenu ? 'rotate(-45deg) translateY(0px) ' : 'translateY(4px)'}
				style:background-color={'var(--burgundy)'}
			></div>
		</button>
	</div>
	<Blur />
</nav>

<style>
	.logoNav {
		pointer-events: none;
		width: 100%;
		padding: 0;
		left: 0;
		background: linear-gradient(rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%);
		height: 100px;
		position: fixed;
		top: 0px;
		z-index: 100;
		width: 100%;
		display: flex;
		justify-content: center;
	}

	.logo-container {
		display: flex;
		align-items: center;
		justify-content: start;
	}

	@media (max-width: 980px) {
		.logo {
			height: 20px;
			width: 70px;
		}
	}

	.container {
		pointer-events: auto;
		width: 100%;
		position: fixed;
		z-index: 100;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--spacing-s) var(--spacing-m);
		max-width: var(--max-width);
	}

	.navLink {
		text-decoration: none;
		color: var(--burgundy);
		font-weight: 700;
		align-self: center;
	}

	.navLink:hover {
		color: var(--purple);
	}
	.navigation {
		display: flex;
		flex-direction: row;
		align-items: stretch;
		gap: calc(var(--gap) * 1);
		border-radius: 200px;
		/* font-size: 0.9em; */
	}

	.burgerMenu {
		display: none;
	}

	.line {
		width: 25px;
		height: 2px;
		background-color: var(--burgundy);
		position: absolute;
		border-radius: 1rem;
		transition: all 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
	}
	.selected {
		color: var(--purple);
	}

	.mobileMenu {
		font-size: 1.2em;
		color: white;
		display: flex;
		flex-direction: column;
		gap: var(--gap);
		background-color: unset;
		background:
			linear-gradient(169deg, rgba(255, 255, 255, 0) 48.97%, rgba(255, 255, 255, 0.5) 104.05%),
			linear-gradient(277deg, rgba(234, 245, 242, 0.5) -14.02%, rgba(252, 233, 225, 0.2) 177.17%);
		width: 100%;
		height: 100dvh;
		position: absolute;
		top: 0;
		left: 0;
		z-index: -1;
		padding: var(--spacing-m);
		padding-top: 5rem;
		backdrop-filter: blur(50px);
		-webkit-backdrop-filter: blur(50px);
	}

	.mobileMenu .navLink {
		align-self: flex-start;
	}

	.items-container {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		font-size: 1.2rem;
		flex: 1;
		/* justify-content: center; */
	}
	.pages {
		gap: 1.8rem;
		width: 100%;
		display: flex;
		justify-content: center;
	}
	@media (max-width: 1050px) {
		.navigation {
			display: none;
		}
		.burgerMenu {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 2px;
			display: block;
			width: 30px;
			height: 30px;
			background: transparent;
			border: unset;
			cursor: pointer;
		}
	}
	@media (min-width: 1050px) {
		.mobileMenu {
			display: none;
		}
	}
</style>
